import { useEffect, useState } from "react";
import { Lock, Unlock, Loader2, Users, UserPlus, X, ShieldCheck, Clock, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { authFetch } from "@/lib/api";
import { toast } from "sonner";
import RequestAccessButton from "@/components/RequestAccessButton";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Verrouillage de parcelle désactivé temporairement.
 *
 * Le CTA d'achat ("Verrouiller cette parcelle — X €/mois") n'est plus proposé
 * aux utilisateurs. Tout le reste du panneau reste actif pour les parcelles
 * DÉJÀ verrouillées : bannières d'accès, gestion des collaborateurs par le
 * propriétaire, force-unlock admin.
 *
 * Repasser à `true` pour rétablir la fonctionnalité (l'endpoint backend
 * POST /api/parcels/{id}/lock/checkout est resté en place).
 */
const LOCK_PURCHASE_ENABLED = false;

/**
 * Panneau de verrouillage de parcelle (Phase 3).
 *
 * États affichés selon l'utilisateur :
 *  - Admin : badge "Accès admin" + actions de gestion
 *  - Propriétaire (locked_by) : section "Gérer les accès" (3 gratuits, +2€)
 *  - PRO global : badge "Accès via abonnement PRO"
 *  - Invité avec accès : badge "Accès accordé"
 *  - Aucune : rien (le CTA de verrouillage est désactivé, cf. LOCK_PURCHASE_ENABLED)
 */
export default function ParcelLockPanel({ parcelId, onAccessChanged }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showGrantForm, setShowGrantForm] = useState(false);
  const [grantEmail, setGrantEmail] = useState("");
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [requestMessage, setRequestMessage] = useState("");

  const fetchStatus = async () => {
    try {
      const res = await authFetch(`${API_BASE}/parcels/${parcelId}/lock/status`);
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
        onAccessChanged?.(data.can_access_carnet);
      }
    } catch (e) {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (parcelId) {
      setLoading(true);
      fetchStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parcelId]);

  const handleLock = async () => {
    setBusy(true);
    try {
      const res = await authFetch(`${API_BASE}/parcels/${parcelId}/lock/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ origin_url: window.location.origin }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.locked) {
        // Verrouillage immédiat (admin ou abonné) — pas de paiement
        toast.success("Parcelle verrouillée !");
        await fetchStatus();
      } else if (res.ok && data.url) {
        // Compat : redirection vers un éventuel checkout Stripe
        window.location.href = data.url;
      } else if (res.status === 402) {
        // Non abonné → l'abonnement est requis
        const d = data.detail || {};
        toast.info(d.message || "Un abonnement Véyèr CIEL est requis pour verrouiller une parcelle.");
        window.location.href = "/subscription";
      } else {
        const d = data.detail;
        const msg = typeof d === "string" ? d : (d && d.message) || "Impossible de verrouiller la parcelle.";
        toast.error(msg);
      }
    } catch {
      toast.error("Erreur de connexion");
    }
    setBusy(false);
  };

  const handleLockRequest = async () => {
    setBusy(true);
    try {
      const res = await authFetch(`${API_BASE}/lock-requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ parcel_id: parcelId, message: requestMessage.trim() || null }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success("Demande envoyée. Vous serez prévenu par e-mail dès qu'elle sera traitée.");
        setShowRequestForm(false);
        setRequestMessage("");
        await fetchStatus();
      } else {
        toast.error(typeof data.detail === "string" ? data.detail : "Envoi impossible");
      }
    } catch { toast.error("Erreur de connexion"); }
    setBusy(false);
  };

  const cancelLockRequest = async (requestId) => {
    setBusy(true);
    try {
      const res = await authFetch(`${API_BASE}/lock-requests/${requestId}`, { method: "DELETE" });
      if (res.ok) { toast.success("Demande annulée"); await fetchStatus(); }
      else toast.error("Annulation impossible");
    } catch { toast.error("Erreur de connexion"); }
    setBusy(false);
  };

  const handleUnlock = async (forceAdmin = false) => {
    const msg = forceAdmin
      ? "FORCE-UNLOCK admin : déverrouiller cette parcelle (annule l'abonnement Stripe du propriétaire à la fin de la période et libère la parcelle immédiatement) ?"
      : "Annuler l'abonnement de verrouillage ?\n\nVotre abonnement Stripe sera annulé à la fin de la période payée. La parcelle redevient immédiatement disponible pour un nouveau verrouillage par un autre utilisateur.";
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      const res = await authFetch(`${API_BASE}/parcels/${parcelId}/lock`, { method: "DELETE" });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        toast.success(
          data.by_admin
            ? "✅ Parcelle déverrouillée (force admin)"
            : "✅ Verrouillage annulé. Abonnement Stripe stoppé en fin de période."
        );
        await fetchStatus();
      } else {
        toast.error(data.detail || `Erreur (${res.status})`);
      }
    } catch { toast.error("Erreur réseau"); }
    setBusy(false);
  };

  const handleGrant = async () => {
    if (!grantEmail.trim()) return;
    setBusy(true);
    try {
      const res = await authFetch(`${API_BASE}/parcels/${parcelId}/access/grant`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: grantEmail.trim().toLowerCase(),
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(typeof data.detail === "string" ? data.detail : data.detail?.message || "Erreur");
      } else if (data.status === "granted_free") {
        toast.success(`Accès accordé gratuitement (${data.remaining_free_slots} restant${data.remaining_free_slots > 1 ? "s" : ""})`);
        setGrantEmail("");
        setShowGrantForm(false);
        await fetchStatus();
      } else if (data.status === "payment_required" && data.checkout_url) {
        toast.info("Limite de collaborateurs gratuits atteinte.");
        setTimeout(() => { window.location.href = data.checkout_url; }, 800);
      }
    } catch { toast.error("Erreur"); }
    setBusy(false);
  };

  const handleRevoke = async (userId) => {
    if (!window.confirm("Retirer l'accès de cet utilisateur ?")) return;
    setBusy(true);
    try {
      const res = await authFetch(`${API_BASE}/parcels/${parcelId}/access/${userId}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Accès retiré");
        await fetchStatus();
      } else {
        toast.error("Erreur");
      }
    } catch { toast.error("Erreur"); }
    setBusy(false);
  };

  if (loading) {
    return (
      <div className="p-3 flex justify-center" data-testid="lock-panel-loading">
        <Loader2 className="w-4 h-4 animate-spin" style={{ color: "#2D6A4F" }} />
      </div>
    );
  }
  if (!status) return null;

  const { locked, locked_by_email, is_owner, is_admin, access_reason,
    granted_access = [], free_slots_remaining, free_slots_used,
    paid_slots_used, my_lock_request } = status;

  // ============== BANNIÈRE D'ACCÈS ==============
  let banner = null;
  if (is_admin) {
    banner = (
      <Badge className="w-full justify-center py-2 text-[11px] font-semibold gap-1.5" style={{ background: "#E07A5F18", color: "#E07A5F", border: "none" }}>
        <ShieldCheck className="w-3.5 h-3.5" /> Accès administrateur
      </Badge>
    );
  } else if (is_owner) {
    banner = (
      <Badge className="w-full justify-center py-2 text-[11px] font-semibold gap-1.5" style={{ background: "#D4A37320", color: "#8B5E3C", border: "none" }}>
        <Lock className="w-3.5 h-3.5" /> Vous êtes propriétaire
      </Badge>
    );
  } else if (access_reason === "global_pro") {
    banner = (
      <Badge className="w-full justify-center py-2 text-[11px] font-semibold gap-1.5" style={{ background: "#2D6A4F18", color: "#2D6A4F", border: "none" }}>
        <ShieldCheck className="w-3.5 h-3.5" /> Accès PRO global
      </Badge>
    );
  } else if (access_reason === "granted") {
    banner = (
      <Badge className="w-full justify-center py-2 text-[11px] font-semibold gap-1.5" style={{ background: "#2D6A4F18", color: "#2D6A4F", border: "none" }}>
        <ShieldCheck className="w-3.5 h-3.5" /> Accès accordé par le propriétaire
      </Badge>
    );
  } else if (locked) {
    banner = (
      <div className="p-3 rounded-lg text-xs space-y-2" style={{ background: "#E07A5F12", color: "#4A4A4A" }} data-testid="lock-no-access">
        <div>
          <p className="font-semibold mb-1" style={{ color: "#E07A5F" }}>Parcelle verrouillée</p>
          <p>Propriétaire : <span className="font-mono">{locked_by_email}</span></p>
        </div>
        <RequestAccessButton parcelId={parcelId} onSent={fetchStatus} />
      </div>
    );
  }

  // Sans le CTA de verrouillage, une parcelle libre consultée par un utilisateur
  // ordinaire n'a plus rien à afficher : on ne rend rien plutôt qu'un bloc vide
  // (qui laisserait la marge du conteneur parent).
  const showLockCta = LOCK_PURCHASE_ENABLED && !locked && !is_admin;
  const showLockRequest = !LOCK_PURCHASE_ENABLED && !locked && !is_admin;
  const showAdminUnlock = is_admin && locked && !is_owner;
  if (!banner && !showLockCta && !showLockRequest && !is_owner && !showAdminUnlock) return null;

  return (
    <div className="space-y-3" data-testid="lock-panel">
      {banner}

      {/* CTA : verrouiller (si pas locked et utilisateur non-admin non-pro) */}
      {LOCK_PURCHASE_ENABLED && !locked && !is_admin && (
        <Button
          className="w-full h-9 text-xs font-semibold rounded-lg gap-1.5 text-white"
          style={{ background: "#2D6A4F" }}
          onClick={handleLock}
          disabled={busy}
          data-testid="lock-btn"
        >
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Lock className="w-3.5 h-3.5" />}
          Verrouiller cette parcelle
        </Button>
      )}

      {/* Demande de verrouillage (validée par un administrateur) */}
      {showLockRequest && (
        my_lock_request?.status === "pending" ? (
          <div className="p-3 rounded-lg text-xs space-y-2" style={{ background: "#D4A37318", color: "#4A4A4A" }} data-testid="lock-request-pending">
            <p className="font-semibold flex items-center gap-1.5" style={{ color: "#8B5E3C" }}>
              <Clock className="w-3.5 h-3.5" /> Demande de verrouillage en attente
            </p>
            <p>Envoyée le {new Date(my_lock_request.created_at).toLocaleDateString("fr-FR")}. Vous serez prévenu par e-mail dès qu'elle sera traitée.</p>
            <button onClick={() => cancelLockRequest(my_lock_request.request_id)} disabled={busy} className="text-[11px] underline" style={{ color: "#7A7A7A" }} data-testid="lock-request-cancel">
              Annuler la demande
            </button>
          </div>
        ) : (
          <div className="rounded-xl border border-black/5 bg-white p-3 space-y-2" data-testid="lock-request-panel">
            {my_lock_request?.status === "rejected" && (
              <p className="text-[11px] p-2 rounded-lg" style={{ background: "#E07A5F12", color: "#B4532F" }}>
                Votre précédente demande n'a pas été acceptée{my_lock_request.reason ? ` : ${my_lock_request.reason}` : "."}
              </p>
            )}
            <p className="text-[11px]" style={{ color: "#4A4A4A" }}>
              C'est votre parcelle ? Demandez son verrouillage : un administrateur valide la demande et la parcelle est rattachée à votre compte.
            </p>
            {showRequestForm ? (
              <div className="space-y-2">
                <Input
                  value={requestMessage}
                  onChange={(e) => setRequestMessage(e.target.value)}
                  maxLength={500}
                  placeholder="Message (facultatif) : n° d'exploitation, précisions…"
                  className="h-8 text-xs"
                  data-testid="lock-request-message"
                />
                <div className="flex gap-1.5">
                  <Button size="sm" className="flex-1 h-8 text-[11px] font-semibold rounded-lg gap-1.5 text-white" style={{ background: "#2D6A4F" }} onClick={handleLockRequest} disabled={busy} data-testid="lock-request-submit">
                    {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Envoyer la demande
                  </Button>
                  <Button size="sm" variant="outline" className="h-8 text-[11px] rounded-lg" onClick={() => { setShowRequestForm(false); setRequestMessage(""); }}>
                    Annuler
                  </Button>
                </div>
              </div>
            ) : (
              <Button className="w-full h-9 text-xs font-semibold rounded-lg gap-1.5 text-white" style={{ background: "#2D6A4F" }} onClick={() => setShowRequestForm(true)} data-testid="lock-request-btn">
                <Lock className="w-3.5 h-3.5" /> Demander le verrouillage
              </Button>
            )}
          </div>
        )
      )}

      {/* Propriétaire : gestion des accès */}
      {is_owner && (
        <div className="rounded-xl border border-black/5 bg-white p-3 space-y-3" data-testid="lock-owner-panel">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold" style={{ color: "#4A4A4A" }}>
              <Users className="w-3.5 h-3.5" /> Accès partagés
            </div>
            <Badge variant="outline" className="text-[9px]" data-testid="lock-slot-counter">
              {free_slots_used}/3 gratuit · {paid_slots_used} payant
            </Badge>
          </div>

          {granted_access.length === 0 ? (
            <p className="text-xs italic" style={{ color: "#7A7A7A" }}>Aucun collaborateur invité pour l'instant.</p>
          ) : (
            <ul className="space-y-1.5">
              {granted_access.map((g) => (
                <li key={g.user_id} className="flex items-center gap-2 text-xs p-2 rounded-lg" style={{ background: "#F9F8F6" }} data-testid={`grant-row-${g.user_id}`}>
                  <span className="w-2 h-2 rounded-full" style={{ background: g.paid ? "#E07A5F" : "#2D6A4F" }} />
                  <span className="flex-1 truncate" style={{ color: "#1A1A1A" }}>{g.email || g.user_id}</span>
                  {g.paid && <Badge className="text-[9px]" style={{ background: "#E07A5F18", color: "#E07A5F", border: "none" }}>Payant</Badge>}
                  <button onClick={() => handleRevoke(g.user_id)} className="text-red-500 hover:text-red-700 p-0.5" data-testid={`revoke-${g.user_id}`} disabled={busy}>
                    <X className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          {!showGrantForm ? (
            <Button
              size="sm"
              className="w-full h-8 text-[11px] font-semibold rounded-lg gap-1.5"
              variant="outline"
              onClick={() => setShowGrantForm(true)}
              data-testid="show-grant-form-btn"
            >
              <UserPlus className="w-3.5 h-3.5" />
              {free_slots_remaining > 0
                ? `Inviter un collaborateur (${free_slots_remaining} gratuit${free_slots_remaining > 1 ? "s" : ""})`
                : `Inviter un collaborateur`}
            </Button>
          ) : (
            <div className="space-y-2">
              <Input
                type="email"
                placeholder="email@exemple.com"
                value={grantEmail}
                onChange={(e) => setGrantEmail(e.target.value)}
                className="h-8 text-xs"
                data-testid="grant-email-input"
              />
              <div className="flex gap-1.5">
                <Button
                  size="sm"
                  className="flex-1 h-8 text-[11px] font-semibold rounded-lg text-white"
                  style={{ background: "#2D6A4F" }}
                  onClick={handleGrant}
                  disabled={busy || !grantEmail.trim()}
                  data-testid="grant-submit-btn"
                >
                  {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Accorder"}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-[11px] rounded-lg"
                  onClick={() => { setShowGrantForm(false); setGrantEmail(""); }}
                  data-testid="grant-cancel-btn"
                >
                  Annuler
                </Button>
              </div>
              {free_slots_remaining === 0 && (
                <p className="text-[10px] italic" style={{ color: "#E07A5F" }}>
                  Limite de collaborateurs gratuits atteinte.
                </p>
              )}
            </div>
          )}

          <Button
            size="sm"
            variant="ghost"
            className="w-full h-7 text-[10px] text-red-600 hover:bg-red-50"
            onClick={() => handleUnlock(false)}
            disabled={busy}
            data-testid="unlock-btn"
          >
            <Unlock className="w-3 h-3 mr-1" /> Annuler mon abonnement de verrouillage
          </Button>
        </div>
      )}

      {/* ADMIN : Force unlock d'une parcelle verrouillée par un autre */}
      {is_admin && locked && !is_owner && (
        <div className="rounded-xl border p-3" style={{ borderColor: "#E07A5F30", background: "#E07A5F08" }} data-testid="admin-force-unlock-panel">
          <p className="text-[11px] font-semibold mb-1" style={{ color: "#E07A5F" }}>
            Action administrateur
          </p>
          <p className="text-[10px] mb-2" style={{ color: "#4A4A4A" }}>
            Cette parcelle est verrouillée par <span className="font-mono">{locked_by_email}</span>.
            Vous pouvez forcer son déverrouillage (annule l'abonnement Stripe à la fin de la période).
          </p>
          <Button
            size="sm"
            className="w-full h-8 text-[11px] font-semibold rounded-lg gap-1.5 text-white"
            style={{ background: "#E07A5F" }}
            onClick={() => handleUnlock(true)}
            disabled={busy}
            data-testid="admin-force-unlock-btn"
          >
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Unlock className="w-3.5 h-3.5" />}
            Forcer le déverrouillage (admin)
          </Button>
        </div>
      )}
    </div>
  );
}
